import React, { useEffect, useState } from "react";
import {
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  TextField,
  InputAdornment,
  Pagination,
  FormControl,
  MenuItem,
  Select,
  Box,
} from "@mui/material";
import { Visibility, Delete, Edit, Search, UploadFile } from "@mui/icons-material";
import useFetchObjects from "../../../../../api/useFetchObjects";
import { useAuth } from "../../../../../context";
import useDelete from "../../../../../api/useDelete";
import EditMedicine from "../editMedicine/EditMedicine";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";

const ListMedicine = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const token = auth?.user?.token;

  // Pagination States
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalMedicines, setTotalMedicines] = useState(0);

  // Fetch Medicines with Pagination
  const { data, refetch, isLoading, isError } = useFetchObjects(
    ["medicines"],
    `medicines/?search=${encodeURIComponent(
      searchQuery
    )}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  // Fetch Medicine Categories
  const { data: categoryData } = useFetchObjects(
    ["medicine-categories"],
    "medicine-categories",
    token
  );

  // Dialog and Snackbar States
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openDescriptionDialog, setOpenDescriptionDialog] = useState(false);
  const [description, setDescription] = useState("");

  // Import from Excel
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = React.useRef(null);

  const handleImportExcel = async () => {
    if (!importFile || !token) return;
    setImporting(true);
    const formData = new FormData();
    formData.append("file", importFile);
    try {
      const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
      const res = await axios.post(`${base}/medicines/import-excel/`, formData, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": false, // allow axios to set multipart/form-data with boundary for FormData
        },
        skipGlobalErrorHandler: true,
      });
      const { created = 0, updated = 0, stock_created = 0, stock_updated = 0, errors } = res.data;
      let msg = t("pharmacy.importSuccess", { created, updated });
      if (stock_created > 0 || stock_updated > 0) {
        msg += " " + t("pharmacy.importStockSuccess", { created: stock_created, updated: stock_updated });
      }
      if (errors?.length) {
        msg += " " + t("pharmacy.importErrors", { count: errors.length });
      }
      toast.success(msg);
      setImportFile(null);
      if (importInputRef.current) importInputRef.current.value = "";
      refetch();
      queryClient.invalidateQueries({ queryKey: ["stock-medicines"] });
    } catch (err) {
      const msg = err.response?.data?.error || err.message || t("pharmacy.importFailed", "Import failed.");
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  // Delete Handler
  const { handleDelete, ConfirmDialog } = useDelete("medicines", token);

  // Handlers for Update Dialog
  const handleOpenUpdateDialog = (medicine) => {
    setSelectedMedicine(medicine);
    setOpenUpdateDialog(true);
  };

  const handleCloseUpdateDialog = () => {
    setOpenUpdateDialog(false);
    setSelectedMedicine(null);
  };

  // Handlers for Description Dialog
  const handleClickOpenDescriptionDialog = (desc) => {
    setDescription(desc);
    setOpenDescriptionDialog(true);
  };

  const handleCloseDescriptionDialog = () => {
    setOpenDescriptionDialog(false);
    setDescription("");
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  useEffect(() => {
    if (data && typeof data.count === "number") {
      setTotalMedicines(data.count);
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, [refetch, searchQuery, page, rowsPerPage]);

  return (
    <Grid item xs={12}>
      <Paper sx={{ boxShadow: "none", m: "1em 3em" }}>
        <Grid container padding={"20px"}>
          {/* Header Section with Search Bar */}
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h5">{t("pharmacy.medicine_list")}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Import from Excel */}
              <Paper variant="outlined" sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("pharmacy.importFromExcel", "Import from Excel")}
                </Typography>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  style={{ display: "none" }}
                  id="medicine-import-file"
                />
                <label htmlFor="medicine-import-file">
                  <Button
                    variant="outlined"
                    size="small"
                    component="span"
                    startIcon={<UploadFile />}
                    disabled={importing}
                  >
                    {t("pharmacy.selectFile", "Select file")}
                  </Button>
                </label>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleImportExcel}
                  disabled={!importFile || importing}
                >
                  {importing ? t("pharmacy.importing", "Importing...") : t("pharmacy.import", "Import")}
                </Button>
                {importFile && (
                  <Typography variant="caption" color="text.secondary" title={t("pharmacy.importExcelHint", "Columns: product_name, category; for stock add: expiry_date, sell_price, quantity")}>
                    {importFile.name}
                  </Typography>
                )}
                <Button
                  variant="text"
                  size="small"
                  href={`${process.env.PUBLIC_URL || ""}/medicine_import_template.csv`}
                  download="medicine_import_template.csv"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("pharmacy.downloadTemplate", "Download template")}
                </Button>
              </Paper>
              <TextField
              placeholder={t("pharmacy.search_medicine")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: "300px" }}
            />
            </Box>
          </Grid>
          <Divider sx={{ mt: 1, width: "100%" }} />

          {/* Loading, Error, or Data Display */}
          {isLoading ? (
            <Grid container justifyContent="center" sx={{ mt: 4 }}>
              <CircularProgress />
            </Grid>
          ) : isError ? (
            <Grid container justifyContent="center" sx={{ mt: 4 }}>
              <Typography color="error">
                {t("pharmacy.failedToLoad")}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => refetch()}
                sx={{ mt: 2 }}
              >
                {t("pharmacy.retry")}
              </Button>
            </Grid>
          ) : (
            <>
              <TableContainer sx={{ mt: 4 }}>
                <Table sx={{ minWidth: "100%" }} aria-label="medicine table">
                  <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
                    <TableRow>
                      <TableCell align="center">{t("pharmacy.id")}</TableCell>
                      <TableCell align="center">
                        {t("pharmacy.medicineName")}
                      </TableCell>
                      <TableCell align="center">
                        {t("pharmacy.category")}
                      </TableCell>
                      <TableCell align="center">
                        {t("pharmacy.description")}
                      </TableCell>
                      <TableCell align="center">
                        {t("pharmacy.actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                    {data?.results?.length > 0 ? (
                      data.results.map((medicine) => (
                        <TableRow
                          key={medicine.id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell align="center">{medicine.id}</TableCell>
                          <TableCell align="center">{medicine.name}</TableCell>
                          <TableCell align="center">
                            {medicine.category_display}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              onClick={() =>
                                handleClickOpenDescriptionDialog(
                                  medicine.description
                                )
                              }
                            >
                              <Visibility />
                            </IconButton>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              color="primary"
                              onClick={() => handleOpenUpdateDialog(medicine)}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              color="secondary"
                              onClick={() => handleDelete(medicine.id)}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="h6">
                            {t("pharmacy.noMedicinesFound")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination Controls */}
              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={4}
                  mb={2}
                >
                  {/* Rows Per Page Selector */}
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" mr={2}>
                      {t("pharmacy.rowsPerPage")}:
                    </Typography>
                    <FormControl variant="outlined" size="small">
                      <Select
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange}
                        sx={{
                          width: 80,
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                        }}
                      >
                        {[10, 25, 50, 100, 500, 1000].map((rows) => (
                          <MenuItem key={rows} value={rows}>
                            {rows}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Pagination Component */}
                  <Pagination
                    count={Math.ceil(totalMedicines / rowsPerPage)}
                    page={page}
                    onChange={handlePageChange}
                    variant="outlined"
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              </Grid>
            </>
          )}

          {/* Snackbar for Messages */}
          <Snackbar
            open={!!snackbarMessage}
            autoHideDuration={6000}
            onClose={() => setSnackbarMessage("")}
            message={snackbarMessage}
          />

          {/* Confirm Delete Dialog */}
          <ConfirmDialog />

          {/* Edit Medicine Dialog */}
          {selectedMedicine && (
            <EditMedicine
              open={openUpdateDialog}
              onClose={handleCloseUpdateDialog}
              medicine={selectedMedicine}
              categoryData={categoryData}
              token={token}
            />
          )}

          {/* Description Dialog */}
          <Dialog
            open={openDescriptionDialog}
            onClose={handleCloseDescriptionDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>{t("pharmacy.medicineDescription")}</DialogTitle>
            <DialogContent dividers>
              <Typography
                variant="body1"
                sx={{ padding: "1em", fontSize: "1.1em", lineHeight: "1.6" }}
              >
                {description}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ padding: "0.5em 1.5em" }}>
              <Button
                onClick={handleCloseDescriptionDialog}
                color="primary"
                variant="contained"
              >
                {t("pharmacy.close")}
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default ListMedicine;
